import type { PeriodWithSubPeriods } from './period-with-sub-periods';
import type { LocalDate } from '../../../utilities/local-date';
import type { PeriodWithMinutes } from './period-with-minutes';
import { getPeriodsWithMinutes } from './get-periods-with-minutes';
import type { PeriodGroupWithMinutes } from './period-group-with-minutes';
import { finalizeGroup } from './finalize-group';
import type { PeriodRowGroup } from './period-row-group';
import { vacant } from './vacant';

/**
 * Takes a list of periods and returns a list of groups containing single or overlapping periods.
 * The periods are placed in a minimal number of rows, each row containing non-overlapping periods.
 * Groups and periods are given properties for start and end time
 * represented by the number of minutes from year start
 */
export function createPeriodGroupsWithMinutes(periods: PeriodWithSubPeriods[], yearStartDate: LocalDate) {
  // set start and end time in minutes
  const periodsWithMinutes: PeriodWithMinutes[] = getPeriodsWithMinutes(periods, yearStartDate);

  // collect single or overlapping periods in groups
  let prevEndMinutes = 0;
  const groups: PeriodGroupWithMinutes[] = [];
  let group: PeriodGroupWithMinutes = {
    type: '',
    start: '',
    end: '',
    startMinutes: 0,
    endMinutes: 0,
    periods: [],
  };
  periodsWithMinutes.forEach((period, index) => {
    // hvis perioden starter før den forrige sluttet: legg perioden til i gruppen
    if (period.startMinutes < prevEndMinutes) {
      group.periods.push(period);
      // hvis perioden starter etter at den forrige sluttet:
      // legg eventuelt til den akkumulerte gruppen på listen og lag en ny gruppe med perioden
    } else {
      if (group.periods.length) {
        groups.push(finalizeGroup(group));
      }
      group = {
        type: period.type,
        start: period.start,
        end: period.end,
        startMinutes: period.startMinutes,
        endMinutes: period.endMinutes,
        periods: [period],
      };
    }
    if (period.endMinutes > prevEndMinutes) {
      prevEndMinutes = period.endMinutes;
    }
    // hvis dette er den siste perioden og gruppen har innhold: legg til gruppen på listen
    if (index === periodsWithMinutes.length - 1 && group.periods.length) {
      groups.push(finalizeGroup(group));
    }
  });
  // fordel periodene i rader
  const rowGroups: PeriodRowGroup[] = groups.map((g) => {
    const rows: PeriodWithMinutes[][] = [[]];
    g.periods.forEach(function (period) {
      let placed = false;
      rows.forEach(function (r) {
        if (!placed && vacant(r, period)) {
          r.push(period);
          placed = true;
        }
      });
      if (!placed) {
        rows.push([period]);
      }
    });
    return {
      startMinutes: g.startMinutes,
      endMinutes: g.endMinutes,
      rows: rows,
    };
  });
  return rowGroups;
}
